import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import API from "../../utils/API/API";
import { Select2V2 } from "@components/FormsV2";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import React, { useEffect, useState } from "react";
import { Button, Form, FormCheck, Modal, Row, Col } from "react-bootstrap";
import {
  handleAlert,
  snakeToText,
  validateEmails,
} from "@utils/helperFunctions";
import "./style/style.css";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import loadingGif from "../../../../assets/images/SS-icon-checkmark-cropped.gif";
import successVector from "./images/SS-success-vector.png";
import successCircle from "./images/success-circle.png";
import errorVector from "./images/SS-error-vector.png";
import errorCircle from "./images/error-circle.png";
import StationaryModal from "@components/StationaryModal/StationaryModal";
import FolderTreeComponentModal from "@components/FolderTreeComponent/FolderTreeComponentModal";
import useFlags from "@common/hooks/useFlags";
import { MultiSelectAutocomplete } from "@components/FormsV2/MultiSelectAutoComplete";
import {
  V1ProjectDownloadTypeResource,
  V1DocumentRequestResource,
} from "@generated";

interface ProjectDownloadType {
  value: string;
  label: string;
  filename: string;
  type: string;
}

function GenerateDocument({
  orientation,
  show,
  setShow,
  account_slug,
  project_id,
  saveToCloudStorageFlag,
  isStationary,
  authorizationCode,
  projectPassed,
  batchDocumentGenerationEnable,
}) {
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
  const [generateDocumentOptions, setGenerateDocumentOptions] = useState([
    { name: "Download", id: "download" },
    { name: "Email", id: "email" },
    { name: "Save To Cloud Storage", id: "cloud_storage" },
  ]);

  const [documentTypes, setDocumentTypes] = useState<
    V1ProjectDownloadTypeResource[]
  >([]);
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<ProjectDownloadType>({
      label: "",
      value: "",
      filename: "",
      type: "",
    });
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<
    ProjectDownloadType[]
  >([]);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    useState("download");
  const [emailFieldChecked, setEmailFieldChecked] = useState(false);
  const [saveToCloudStorageChecked, setSaveToCloudStorageChecked] =
    useState(false);
  const [emailList, setEmailList] = useState("");
  const [emailsAreInvalid, setEmailsAreInvalid] = useState(false);
  const [emailListChanged, setEmailListChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // From v1/project-documents (no batch gen)
  const [documentId, setDocumentId] = useState(null);
  // From v1/document-requests
  const [documentRequestId, setDocumentRequestId] = useState(null);
  const [documentURL, setDocumentURL] = useState(null);
  const [errorMessages, setErrorMessages] = useState<string | JSX.Element>("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessfulGenerationModal, setshowSuccessfulGenerationModal] =
    useState(false);
  const [accountConnections, setAccountConnections] = useState([]);
  const [showFileTreeModal, setShowFileTreeModal] = useState(false);
  const [connectedApps, setConnectedApps] = useState([]);
  const [storageIntegrations, setStorageIntegrations] = useState<any[]>([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [generatePdf, setGeneratePdf] = useState(false);
  const [forceRegeneration, setForceRegeneration] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [project, setProject] = useState(projectPassed);
  const [selectedFolder, setSelectedFolder] = useState({ id: "0", name: "" });
  const [folderLoading, setFolderLoading] = useState(true);
  const [generationStatus, setGenerationStatus] =
    useState<"loading" | "success" | "error">("loading");
  const [step, setStep] = useState<number>(1);

  const storageOptions = project?.attributes?.["storage-options"];

  useEffect(() => {
    if (authorizationCode !== "") {
      //Check for docusign
      API.Get(`${apiHost}/v1/accounts/${account_slug}`, authorizationCode).then(
        (res) => {
          const documentGenerateOptions = saveToCloudStorageFlag
            ? generateDocumentOptions
            : generateDocumentOptions.filter(
                (option) => option.name !== "Save To Cloud Storage"
              );
          if (res.data.data.attributes["esignature?"]) {
            if (
              !generateDocumentOptions.find(
                (option) => option.name === "Electronic Signature"
              )
            ) {
              setGenerateDocumentOptions([
                ...documentGenerateOptions,
                { name: "Electronic Signature", id: "docusign" },
              ]);
            }
          } else {
            setGenerateDocumentOptions(documentGenerateOptions);
          }
          setIsLoading(false);
        }
      );

      //GET ProjectDownloadTypes
      API.Get(
        `${apiHost}/${account_slug}/v1/projects/${project_id}/project-download-types`,
        authorizationCode
      ).then((response) => {
        const documentTypes = response.data.data;
        setDocumentTypes(documentTypes);
        let selectedDocumentType = {
          value: documentTypes[0].attributes["template-id"],
          label: documentTypes[0].attributes.name,
          filename: documentTypes[0].attributes.filename,
          type: documentTypes[0].attributes["document-type"],
        };
        if (project?.relationships?.["document-template"]?.data?.id) {
          const foundTemplate = documentTypes.find(
            (template) =>
              template?.id ==
              project?.relationships?.["document-template"]?.data?.id
          );
          if (foundTemplate) {
            selectedDocumentType = {
              value: foundTemplate?.attributes?.["template-id"],
              label: foundTemplate?.attributes?.name,
              filename: foundTemplate.attributes?.filename,
              type: foundTemplate?.attributes["document-type"],
            };
          }
        }
        setSelectedDocumentType(selectedDocumentType);
        setSelectedDocumentTypes([selectedDocumentType]);
        setFilename(documentTypes[0].attributes?.filename);
        setIsLoading(false);
      });

      if (saveToCloudStorageFlag) {
        // GET Account Connections
        API.Get(
          `${apiHost}/${account_slug}/v1/account-connections`,
          authorizationCode
        ).then((response) => {
          const accountConnections = response?.data?.data;
          setAccountConnections(accountConnections);
          const mergeDevAccountConnections = accountConnections.filter(
            (ac) => ac.attributes.service === "merge_dev"
          );

          // GET Connected Apps
          API.Get(
            `${apiHost}/${account_slug}/v1/connected-apps`,
            authorizationCode
          ).then((res) => {
            const connectedApps = res?.data?.data;
            let mergeDevConnectedApp = connectedApps.find(
              (ac) => ac.attributes.service === "merge_dev"
            );
            let storageIntegrations: any[] = [];
            mergeDevAccountConnections.forEach((ac) => {
              mergeDevConnectedApp?.attributes?.providers.forEach(
                (provider) => {
                  if (
                    provider.name
                      .toLowerCase()
                      .includes(ac?.attributes?.settings.storage)
                  ) {
                    storageIntegrations.push({
                      provider: provider,
                      connection: ac,
                    });
                  }
                }
              );
            });
            setConnectedApps(connectedApps);
            setStorageIntegrations(storageIntegrations);
            let accountConnection;
            let selectedIntegration;
            if (storageOptions && Object.keys(storageOptions).length !== 0) {
              accountConnection = accountConnections.find(
                (ac) => ac.id == Object.keys(storageOptions)[0]
              );
              selectedIntegration = storageIntegrations.find((si) =>
                si.provider.name
                  .toLowerCase()
                  .includes(accountConnection?.attributes?.settings.storage)
              );
            } else {
              accountConnection = storageIntegrations[0].connection;
              selectedIntegration = storageIntegrations[0];
            }
            setSelectedConnection(accountConnection);
            setSelectedIntegration(selectedIntegration);
          });
        });
      }
    }
  }, []);

  useEffect(() => {
    const checkForDocURL = setInterval(() => {
      if (documentId !== null && selectedDeliveryMethod !== "docusign") {
        API.Get(
          `${apiHost}/${account_slug}/v1/project-documents/${documentId}`,
          authorizationCode
        ).then((res) => {
          // Stop polling once the document has moved on from 'created'
          if (res.data.data.attributes.status !== "created") {
            clearInterval(checkForDocURL);
            if (res.data.data.attributes["document-url"] !== null) {
              setDocumentURL(res.data.data.attributes["document-url"]);
              setGenerationStatus("success");
            }
            if (res.data.data.attributes.status === "failed") {
              let errorObj = res.data.data.attributes["error-text"];

              if (typeof errorObj === "string") {
                try {
                  errorObj = JSON.parse(errorObj);
                } catch (e) {
                  setErrorMessages(errorObj);
                  setGenerationStatus("error");
                  return;
                }
              }
              const firstKey = Object.keys(errorObj)[0];
              const firstMessage = errorObj[firstKey][0];

              setErrorMessages(firstMessage);
              setGenerationStatus("error");
            }
          }
        });
      } else {
        clearInterval(checkForDocURL);
      }
    }, 5000);
  }, [documentId]);

  useEffect(() => {
    const checkForDocURL = setInterval(() => {
      if (documentRequestId !== null) {
        API.Get(
          `${apiHost}/${account_slug}/v1/document-requests/${documentRequestId}?include=project-documents`,
          authorizationCode
        ).then((res) => {
          const documentRequest = res.data.data;

          // Stop polling once the document has moved on from 'created'
          if (documentRequest.attributes.status === "finished") {
            clearInterval(checkForDocURL);
            setDocumentURL(documentRequest.attributes?.["document-url"]);
            setGenerationStatus("success");
          } else if (documentRequest.attributes.status === "failed") {
            clearInterval(checkForDocURL);
            const projectDocuments = documentRequest.relationships?.[
              "project-documents"
            ]?.data?.map((pj) =>
              res.data?.included?.find((i) => i.id == pj.id)
            );
            setErrorMessages(
              <ul>
                {projectDocuments.map((document, i) => {
                  const atts = document.attributes;
                  const error = atts?.["error-text"] || "";
                  const status = atts?.status;
                  const name =
                    atts?.["template-name"] || atts?.["document-type"];
                  const url = atts?.["document-url"];
                  const isFinished = status === "finished";
                  return (
                    <li
                      style={{
                        textAlign: "start",
                        color: isFinished ? "#1c2655" : "#B00020",
                      }}
                      key={i}
                    >
                      <strong>{name}: </strong>
                      <span>
                        {status} - {error}
                      </span>
                      {isFinished && url && (
                        <a className="fileDownload" href={url} target="_blank">
                          Download
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            );
            setGenerationStatus("error");
          }
        });
      } else {
        clearInterval(checkForDocURL);
      }
    }, 5000);
  }, [documentRequestId]);

  const renderProjectDownLoadOptions = () => {
    let options = new Set();
    let documentGroups = new Set();
    documentTypes.forEach((documentType) => {
      documentGroups.add(documentType?.["attributes"]?.["group"]);
    });

    Array.from(documentGroups).forEach((docGroup) => {
      options.add({
        label: docGroup,
        options: documentTypes
          .filter((docType) => docType?.["attributes"]?.["group"] === docGroup)
          .map((dt) => {
            return {
              label: dt?.["attributes"]?.["name"],
              value: dt?.["attributes"]?.["template-id"],
              filename: dt?.attributes?.filename,
              type: dt?.attributes?.["document-type"],
            };
          }),
      });
    });
    return Array.from(options);
  };
  const closeModal = () => {
    setStep(1);
    setShow(false);
    setShowFileTreeModal(false);
    setEmailList("");
    setEmailListChanged(false);
    setForceRegeneration(false);
    setGeneratePdf(false);
  };
  const closeSuccessModal = () => {
    setDocumentId(null);
    setDocumentRequestId(null);
    setDocumentURL(null);
    setshowSuccessfulGenerationModal(false);
    setSelectedDeliveryMethod("download");
    setEmailFieldChecked(false);
    setSaveToCloudStorageChecked(false);
  };
  const handleErrorResponse = (res) => {
    let errorObj = res.data.data.attributes["error-text"];

    if (typeof errorObj === "string") {
      try {
        errorObj = JSON.parse(errorObj);
      } catch (e) {
        setErrorMessages(errorObj);
        setGenerationStatus("error");
        return;
      }
    }
    const firstKey = Object.keys(errorObj)[0];
    const firstMessage = errorObj[firstKey][0];

    setErrorMessages(firstMessage);
    setGenerationStatus("error");
  };

  const generateDocument = async (e) => {
    e.preventDefault();
    setGenerationStatus("loading");
    setshowSuccessfulGenerationModal(true);

    closeModal();

    if (batchDocumentGenerationEnable) {
      const data = {
        type: "document-requests",
        attributes: {
          "force-regeneration": forceRegeneration,
          "generate-pdf": generatePdf,
          "delivery-type": selectedDeliveryMethod,
          "email-list":
            selectedDeliveryMethod === "email" ? emailList : undefined,
          filepath:
            selectedDeliveryMethod == "cloud_storage"
              ? selectedFolder?.name
              : undefined,
          templates: selectedDocumentTypes.map((dt) => {
            return {
              document_type: dt.type,
              template_id: dt.value,
              filename: dt.filename,
            };
          }),
        },
        relationships: {
          project: { data: { id: project_id, type: "projects" } },
        },
      } as V1DocumentRequestResource;

      try {
        const promise = await API.Post(
          `${apiHost}/${account_slug}/v1/document-requests`,
          { data },
          authorizationCode
        );
        const documentRequest = promise.data.data;
        setDocumentRequestId(documentRequest.id);
      } catch (err) {
        //@ts-ignore
        const exception = err?.response?.data?.errors?.[0]?.meta?.exception;
        //@ts-ignore
        const message = err?.message || "Failed to create document-request";
        setErrorMessages(exception ? `${message}: ${exception}` : message);
        setGenerationStatus("error");
      }
    } else {
      let data = {
        type: "project-documents",
        attributes: {
          "document-type": selectedDocumentType.type,
          "delivery-type": selectedDeliveryMethod,
          "generate-pdf": generatePdf,
          "template-id": selectedDocumentType.value,
          "force-regeneration": forceRegeneration,
        },
        relationships: {
          project: { data: { id: project_id, type: "projects" } },
        },
      };

      if (selectedDeliveryMethod == "email") {
        if (!validateEmails(emailList) || emailList === "") {
          return;
        }
        data.attributes["email-list"] = emailList;
      }

      if (selectedDeliveryMethod == "cloud_storage") {
        data.attributes["filepath"] = selectedFolder?.name;
        data.attributes["filename"] = filename;
        data.attributes["account-connection-id"] = selectedConnection?.["id"];
      }

      API.Post(
        `${apiHost}/${account_slug}/v1/project-documents`,
        { data },
        authorizationCode
      ).then((res) => {
        if (res.data.data.attributes.status === "failed") {
          handleErrorResponse(res);
        } else {
          if (selectedDeliveryMethod === "docusign") {
            setSuccessMessage("Submitted to Docusign.");
            setShowSuccessAlert(true);
          } else {
            setDocumentId(res.data.data.id);
          }
        }
      });
    }

    API.Get(
      `${apiHost}/${account_slug}/v1/projects/${project_id}?include=account,business-unit,external-request,project-locations,payment-term,client,rate-table,psa-project,document-template`,
      authorizationCode
    ).then((res) => {
      const project = res?.data?.data;
      setProject(project);
    });
  };
  const getGenerateForm = () => {
    return step === 1 ? (
      <Form
        style={{
          display: "flex",
          flexDirection: orientation === "horizontal" ? "row" : "column",
        }}
        className="generateForm"
      >
        <div style={{ width: "400px" }}>
          <div className="text20">Generation Method</div>
          <p>What type of document would you like to generate?</p>
          {batchDocumentGenerationEnable ? (
            <MultiSelectAutocomplete
              label="*Document Type"
              placeholder="Search and select multiple document types"
              className="documentTypes"
              options={
                renderProjectDownLoadOptions() as {
                  value: string;
                  label: string;
                }[]
              }
              value={selectedDocumentTypes}
              onChange={(e) => {
                //@ts-ignore
                setSelectedDocumentTypes(e || []);
              }}
              required={true}
            />
          ) : (
            <Select2V2
              label={"*Document Type"}
              options={renderProjectDownLoadOptions()}
              isDisabled={false}
              onChange={(e) => {
                setSelectedDocumentType(e);
                const documentType = documentTypes.find(
                  (docType) => docType.id == e.value
                );
                setFilename(documentType?.attributes?.["filename"] || "");
                if (e?.type && e.type !== "sow") {
                  setGeneratePdf(false);
                  if (selectedDeliveryMethod === "docusign") {
                    setSelectedDeliveryMethod("download");
                  }
                }
              }}
              value={selectedDocumentType}
            />
          )}
          {documentTypes?.find(
            (documentType) =>
              String(documentType?.attributes?.["template-id"]) ===
              String(selectedDocumentType?.value)
          )?.attributes?.["document-type"] === "sow" && (
            <Form.Check
              style={{ color: "#418172", marginTop: "10px" }}
              type="checkbox"
              id="generatePdfCheck"
              label="Convert Word Document to PDF?"
              checked={generatePdf}
              onChange={(e) => {
                setGeneratePdf(e.target.checked);
              }}
            />
          )}
          <FormFieldLabel
            className="marginTop10"
            label="How would you like to deliver this document?"
          />
          {generateDocumentOptions.map((item, index) => {
            if (
              documentTypes?.find(
                (documentType) =>
                  documentType?.attributes?.["template-id"] ==
                  String(selectedDocumentType?.value)
              )?.attributes?.["document-type"] !== "sow" &&
              item.name === "Electronic Signature"
            ) {
              return null;
            } else {
              return (
                <FormCheck
                  checked={item?.id === selectedDeliveryMethod ? true : false}
                  required={true}
                  id={item.id}
                  className="deliveryCheckbox"
                  key={index}
                  label={item.name}
                  name="group1"
                  type="radio"
                  onChange={(e) => {
                    setSelectedDeliveryMethod(e?.target?.id);
                    if (e.target.id == "email") {
                      setEmailFieldChecked(true);
                    } else {
                      setEmailFieldChecked(false);
                    }
                    if (e.target.id == "cloud_storage") {
                      setSaveToCloudStorageChecked(true);
                    } else {
                      setSaveToCloudStorageChecked(false);
                    }
                  }}
                />
              );
            }
          })}
          <Form.Check
            style={{
              color: "#418172",
              marginTop: "2em",
              minWidth: "165px",
              marginBottom: "15px",
            }}
            type="checkbox"
            id="forceRegenerationCheck"
            label="Force regeneration?"
            checked={forceRegeneration}
            onChange={(e) => {
              setForceRegeneration(e.target.checked);
            }}
          />
        </div>
        {emailFieldChecked && (
          <div
            className={
              orientation === "horizontal"
                ? "emailSectionHorizontal"
                : "emailSectionVertical"
            }
          >
            <div className="text20">Send by Email</div>
            <p>Where would you like the generated document sent?</p>
            <Form.Group className="formGroup">
              <FormFieldLabel
                className={""}
                label="*Email List (comma separated list of emails)"
              />
              <Form.Control
                required={true}
                value={emailList}
                onChange={(e) => {
                  setEmailListChanged(true);
                  setEmailList(e.target.value);
                }}
                isInvalid={emailsAreInvalid && emailListChanged}
                onBlur={() =>
                  setEmailsAreInvalid(
                    (!validateEmails(emailList) || emailList === "") &&
                      selectedDeliveryMethod === "email"
                  )
                }
              />
              {emailsAreInvalid && (
                <Form.Control.Feedback type="invalid">
                  Please enter a valid list of emails
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </div>
        )}
        {saveToCloudStorageChecked && (
          <div
            className={
              orientation === "horizontal"
                ? "emailSectionHorizontal"
                : "emailSectionVertical"
            }
          >
            <div className="text20">Save To Cloud Storage</div>
            <p>Choose your cloud storage provider.</p>
            <div className="flexCenter" style={{ marginTop: "-20px" }}>
              {storageIntegrations.map((si) => {
                return (
                  <img
                    onClick={() => {
                      setSelectedConnection(si?.connection);
                      setFolderLoading(true);
                    }}
                    key={si?.provider?.name}
                    style={
                      selectedConnection?.["id"] == si?.connection?.id
                        ? {
                            border: "2px solid #418172",
                            width: "110px",
                            cursor: "pointer",
                            borderRadius: "7%",
                            margin: "5px",
                            padding: "5px",
                          }
                        : { width: "110px", cursor: "pointer", padding: "5px" }
                    }
                    src={si?.provider?.logo}
                  />
                );
              })}
            </div>
            {!folderLoading ? (
              <Form.Group className="formGroup">
                <FormFieldLabel
                  className={""}
                  label={
                    selectedConnection
                      ? `*${snakeToText(
                          selectedConnection?.["attributes"]?.["settings"]?.[
                            "storage"
                          ]
                        )} Storage Folder`
                      : "*Storage Folder"
                  }
                />
                {selectedFolder ? (
                  <Form.Control
                    type="text"
                    readOnly={true}
                    value={selectedFolder?.name}
                  />
                ) : (
                  <SmallSpinner />
                )}
                <small
                  onClick={() => setShowFileTreeModal(true)}
                  className="editRoot"
                >
                  Edit Storage Folder
                </small>
              </Form.Group>
            ) : (
              <SmallSpinner />
            )}
            {!batchDocumentGenerationEnable && (
              <Form.Group className="formGroup">
                <FormFieldLabel className={""} label="*Generated File Name" />
                <Form.Control
                  required={true}
                  value={filename ? filename : ""}
                  onChange={(e) => {
                    setFilename(e.target.value);
                  }}
                  isInvalid={false}
                />
              </Form.Group>
            )}
          </div>
        )}
        {isStationary && (
          <Button
            style={
              selectedDeliveryMethod !== "email" ? { marginTop: "20px" } : {}
            }
            onClick={(e) =>
              step === 1 && batchDocumentGenerationEnable
                ? setStep(2)
                : generateDocument(e)
            }
            className="seafoamBgButton"
            disabled={
              ((!validateEmails(emailList) || emailList === "") &&
                emailFieldChecked) ||
              (saveToCloudStorageChecked &&
                (selectedFolder.id === "" || !filename)) ||
              (batchDocumentGenerationEnable &&
                selectedDocumentTypes.length === 0)
            }
          >
            Generate
          </Button>
        )}
      </Form>
    ) : (
      <div className="docGenFileNaming">
        <div className="text20">File Naming </div>
        <p style={{ marginTop: "5px" }}>
          View / edit filenames for each document type selected.
        </p>
        <div className="filenames">
          {selectedDocumentTypes.map((dt) => {
            return (
              <div key={dt.value} className="docType">
                <div
                  style={{
                    marginTop: "10px",
                    display: "flex",
                    color: "#418172",
                    marginBottom: "5px",
                  }}
                >
                  <div style={{ fontWeight: "700", marginRight: "5px" }}>
                    *Filename
                  </div>
                  <div>({dt.label})</div>
                </div>
                <Form.Control
                  required={true}
                  value={dt.filename}
                  onChange={(e) => {
                    const updated = selectedDocumentTypes.map((sdt) =>
                      sdt.value === dt.value
                        ? { ...sdt, filename: e.target.value }
                        : sdt
                    );
                    setSelectedDocumentTypes(updated);
                  }}
                  isInvalid={false}
                />
              </div>
            );
          })}
        </div>
        {isStationary && (
          <div
            style={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={(e) => setStep(1)}
              variant="secondary"
              className="btnSeawhite"
              style={{ width: "45%" }}
            >
              Back
            </Button>
            <Button
              onClick={(e) => generateDocument(e)}
              className="seafoamBgButton"
              disabled={
                selectedDocumentTypes.find((dt) => !dt.filename) ? true : false
              }
              style={{ width: "45%" }}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {isStationary ? (
        <StationaryModal
          header={"Generate Document"}
          body={isLoading ? <SmallSpinner /> : getGenerateForm()}
        />
      ) : (
        <ScopeStackModal
          className={
            orientation === "horizontal" &&
            (emailFieldChecked || saveToCloudStorageChecked)
              ? "wide "
              : ""
          }
          modalTitle={"Generate Document"}
          modalBody={isLoading ? <SmallSpinner /> : getGenerateForm()}
          button1Text={step === 1 ? "Cancel" : "Back"}
          handleButton1Click={() => (step === 1 ? closeModal() : setStep(1))}
          button2Text={step === 1 ? "Generate" : "Save"}
          handleButton2Click={(e) =>
            step === 1 && batchDocumentGenerationEnable
              ? setStep(2)
              : generateDocument(e)
          }
          button2Disabled={
            ((!validateEmails(emailList) || emailList === "") &&
              emailFieldChecked) ||
            (saveToCloudStorageChecked &&
              (selectedFolder?.id === "" || !filename)) ||
            (batchDocumentGenerationEnable &&
              selectedDocumentTypes.length === 0)
          }
          show={show}
          handleClose={() => closeModal()}
        />
      )}
      <Modal
        centered
        show={showSuccessfulGenerationModal}
        onHide={closeSuccessModal}
      >
        <Modal.Body className="genDocModal">
          {(() => {
            switch (generationStatus) {
              case "success":
                return (
                  <div>
                    <div className="headerWithX">
                      <p className="genDocTitle">Generate Document</p>
                      <div
                        className="XIcon"
                        onClick={closeSuccessModal}
                        style={{ cursor: "pointer" }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                        >
                          <path
                            d="M19 6.91L17.59 5.5L12 11.09L6.41 5.5L5 6.91L10.59 12.5L5 18.09L6.41 19.5L12 13.91L17.59 19.5L19 18.09L13.41 12.5L19 6.91Z"
                            fill="#1C2655"
                          />
                        </svg>
                      </div>
                    </div>
                    <hr />
                    <div className="modalImageWithText">
                      {/* The div below(modalDisplayImages) is the graphic for the success modal */}
                      <div className="modalDisplayImages">
                        <img src={successVector} style={{ display: "block" }} />
                        <img
                          src={successCircle}
                          style={{
                            position: "absolute",
                            top: "0",
                            left: "60%",
                            display: "block",
                            transform: "translate(5%, 180%)",
                          }}
                        />
                      </div>
                      <p className="genDocWordBody">
                        {selectedDeliveryMethod === "download"
                          ? "Document generated successfully!"
                          : "Document generated and sent successfully!"}
                      </p>
                    </div>
                  </div>
                );
              case "error":
                return (
                  <div>
                    <div className="headerWithX">
                      <p className="genDocTitle">Generate Document</p>
                      <div
                        className="XIcon"
                        onClick={closeSuccessModal}
                        style={{ cursor: "pointer" }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                        >
                          <path
                            d="M19 6.91L17.59 5.5L12 11.09L6.41 5.5L5 6.91L10.59 12.5L5 18.09L6.41 19.5L12 13.91L17.59 19.5L19 18.09L13.41 12.5L19 6.91Z"
                            fill="#1C2655"
                          />
                        </svg>
                      </div>
                    </div>
                    <hr />
                    {/* The div below(modalDisplayImages) is the graphic for the error modal */}
                    <div className="modalDisplayImages">
                      <img src={errorVector} style={{ display: "block" }} />
                      <img
                        src={errorCircle}
                        style={{
                          position: "absolute",
                          top: "0",
                          left: "60%",
                          display: "block",
                          transform: "translate(5%, 180%)",
                        }}
                      />
                    </div>
                    <p className="genDocWordBody">
                      There was a problem generating your document. If this
                      error persists, please contact support.
                    </p>
                    <div
                      style={{
                        color: "#B00020",
                        textAlign: "center",
                        padding: "0 16px",
                        whiteSpace: "pre-line",
                        marginTop: "8px",
                      }}
                    >
                      {errorMessages}
                    </div>
                    <hr />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "right",
                        margin: "0px 16px 16px 16px",
                        gap: "8px",
                      }}
                    >
                      <Button
                        className="modalCancelButton"
                        onClick={closeSuccessModal}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="modalDownloadButton"
                        onClick={() => {
                          closeSuccessModal();
                          setShow(true);
                        }}
                      >
                        Try Again
                      </Button>
                    </div>
                  </div>
                );
              case "loading":
              default:
                return (
                  <div>
                    <div className="headerWithX">
                      <p className="genDocTitle">Generate Document</p>
                      <div
                        className="XIcon"
                        onClick={closeSuccessModal}
                        style={{ cursor: "pointer" }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                        >
                          <path
                            d="M19 6.91L17.59 5.5L12 11.09L6.41 5.5L5 6.91L10.59 12.5L5 18.09L6.41 19.5L12 13.91L17.59 19.5L19 18.09L13.41 12.5L19 6.91Z"
                            fill="#1C2655"
                          />
                        </svg>
                      </div>
                    </div>
                    <hr />
                    <div
                      className="GenDocModalBody"
                      style={{ margin: "54px 0px 46px 0px" }}
                    >
                      {/* The div below(modalDisplayImages) is the graphic for the loading modal */}
                      <div className="modalDisplayImages">
                        <img src={loadingGif} style={{ marginTop: "-50px" }} />
                      </div>
                      <p className="genDocWordBody">
                        The document is being generated and will be{" "}
                        {selectedDeliveryMethod === "download"
                          ? "available to download"
                          : "sent"}{" "}
                        as soon as it is finished.
                      </p>
                    </div>
                    <hr />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "right",
                        margin: "0px 16px 16px 16px",
                      }}
                    >
                      <Button className="modalLoadingButton">Generating</Button>
                    </div>
                  </div>
                );
            }
          })()}
        </Modal.Body>
        {documentURL && (
          <Modal.Footer>
            <>
              <Button className="modalCancelButton" onClick={closeSuccessModal}>
                Cancel
              </Button>
              <Button
                className="modalDownloadButton"
                href={documentURL}
                target="_blank"
              >
                Download
              </Button>
            </>
          </Modal.Footer>
        )}
      </Modal>
      <FolderTreeComponentModal
        showFileTreeModal={showFileTreeModal}
        setShowFileTreeModal={setShowFileTreeModal}
        showCheckbox={false}
        readOnly={false}
        indentPixels={40}
        account_slug={account_slug}
        authorizationCode={authorizationCode}
        selectedFolder={selectedFolder}
        setSelectedFolder={setSelectedFolder}
        folderLoading={folderLoading}
        setFolderLoading={setFolderLoading}
        selectedConnection={selectedConnection}
        setSelectedConnection={setSelectedConnection}
        selectedIntegration={selectedIntegration}
        setSelectedIntegration={setSelectedIntegration}
        storageOptions={storageOptions}
        canCreateFolder={false}
        canEditFolderName={false}
        isChooseRootFolder={false}
      />
    </div>
  );
}

export default GenerateDocument;
